import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  useTheme,
  alpha,
  Avatar,
  Stack,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  ShoppingCart as PurchaseIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import { format } from 'date-fns';
import axiosInstance from '../utils/axios';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

const UserBalanceHistory = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [history, setHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Автоматически выбираем пользователя если пришли со страницы пользователей
  useEffect(() => {
    if (location.state?.selectedUserId && users.length > 0) {
      const user = users.find(u => u.userId === location.state.selectedUserId);
      if (user) {
        handleUserSelect(user);
      }
    }
  }, [users, location.state]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/telegram-users');
      // API возвращает массив напрямую, а не в поле data
      const usersData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      enqueueSnackbar('Ошибка загрузки пользователей', { variant: 'error' });
    }
  };

  const fetchUserHistory = async (userId) => {
    setLoading(true);
    try {
      // Получаем историю баланса из BalanceHistory
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const historyResponse = await axiosInstance.get(`/telegram-users/users/${userId}/balance-history`, { params });

      const balanceHistory = Array.isArray(historyResponse.data) ? historyResponse.data : [];
      
      // Преобразуем в нужный формат
      const transactions = balanceHistory.map(entry => ({
        id: entry._id,
        type: entry.type === 'admin_add' ? 'deposit' : 
              entry.type === 'admin_subtract' ? 'admin_subtract' :
              entry.type === 'withdraw' ? 'purchase' : entry.type,
        amount: entry.amount,
        date: new Date(entry.createdAt),
        description: entry.description,
        status: 'paid',
        balanceBefore: entry.balanceBefore,
        balanceAfter: entry.balanceAfter,
        adminUsername: entry.adminUsername
      }));

      setHistory(transactions);
    } catch (error) {
      console.error('Error fetching history:', error);
      enqueueSnackbar('Ошибка загрузки истории', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    fetchUserHistory(user.userId);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(Math.abs(value) || 0);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <IncomeIcon sx={{ color: theme.palette.success.main }} />;
      case 'purchase':
        return <ExpenseIcon sx={{ color: theme.palette.error.main }} />;
      case 'coupon':
        return <PurchaseIcon sx={{ color: theme.palette.warning.main }} />;
      default:
        return <WalletIcon />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'deposit':
        return 'Пополнение (админ)';
      case 'admin_subtract':
        return 'Списание (админ)';
      case 'purchase':
        return 'Покупка';
      case 'coupon':
        return 'Покупка (купон)';
      default:
        return 'Операция';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'deposit':
        return 'success';
      case 'admin_subtract':
        return 'warning';
      case 'purchase':
        return 'error';
      case 'coupon':
        return 'warning';
      default:
        return 'default';
    }
  };

  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.userId?.includes(searchQuery) ||
    user.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalDeposits = history.filter(t => t.type === 'deposit').reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const totalSpent = history.filter(t => (t.type === 'purchase' || t.type === 'admin_subtract')).reduce((sum, t) => sum + Math.abs(t.amount), 0);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {/* Header */}
        <Paper
          elevation={4}
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 3,
            mb: 3,
            color: 'text.primary'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <WalletIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  История баланса пользователей
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Просмотр всех операций с балансом
                </Typography>
              </Box>
            </Box>
            {selectedUser && (
              <Tooltip title="Обновить">
                <IconButton
                  onClick={() => fetchUserHistory(selectedUser.userId)}
                  disabled={loading}
                  sx={{
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Paper>

        <Grid container spacing={3}>
          {/* Left Panel - User List */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                Поиск пользователя
              </Typography>
              
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Поиск по username, ID, имени..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {filteredUsers.map((user) => (
                  <Card
                    key={user._id}
                    onClick={() => handleUserSelect(user)}
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      bgcolor: selectedUser?.userId === user.userId ? alpha(theme.palette.primary.main, 0.1) : 'white',
                      border: selectedUser?.userId === user.userId ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        transform: 'translateY(-2px)',
                        boxShadow: 2
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        @{user.username || 'Без username'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {user.userId}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                        <Typography variant="body2">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Chip
                          label={formatCurrency(user.balance || 0)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Right Panel - History */}
          <Grid item xs={12} md={8}>
            {!selectedUser ? (
              <Paper elevation={2} sx={{ p: 6, borderRadius: 2, textAlign: 'center' }}>
                <WalletIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Выберите пользователя для просмотра истории
                </Typography>
              </Paper>
            ) : (
              <>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={4}>
                    <Card elevation={2}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                          <WalletIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                          <Typography variant="body2" color="text.secondary">
                            Текущий баланс
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight="bold" color="primary">
                          {formatCurrency(selectedUser.balance || 0)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card elevation={2}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                          <IncomeIcon sx={{ mr: 1, color: theme.palette.success.main }} />
                          <Typography variant="body2" color="text.secondary">
                            Всего пополнено
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                          {formatCurrency(totalDeposits)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card elevation={2}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                          <ExpenseIcon sx={{ mr: 1, color: theme.palette.error.main }} />
                          <Typography variant="body2" color="text.secondary">
                            Всего потрачено
                          </Typography>
                        </Box>
                        <Typography variant="h4" fontWeight="bold" color="error.main">
                          {formatCurrency(totalSpent)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Filters */}
                <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Дата начала"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Дата окончания"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small'
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => fetchUserHistory(selectedUser.userId)}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Применить'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {/* History Table */}
                <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" fontWeight="bold">
                      История операций ({history.length})
                    </Typography>
                  </Box>

                  {loading ? (
                    <Box display="flex" justifyContent="center" p={6}>
                      <CircularProgress />
                    </Box>
                  ) : history.length === 0 ? (
                    <Box p={6} textAlign="center">
                      <Typography variant="h6" color="text.secondary">
                        Нет операций
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Дата</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Тип</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Описание</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>Сумма</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {history.map((transaction) => (
                            <TableRow key={transaction.id} hover>
                              <TableCell>
                                <Typography variant="body2">
                                  {format(transaction.date, 'dd.MM.yyyy HH:mm')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  {getTypeIcon(transaction.type)}
                                  <Typography variant="body2" ml={1}>
                                    {getTypeLabel(transaction.type)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {transaction.description}
                                </Typography>
                                {transaction.city && (
                                  <Typography variant="caption" color="text.secondary">
                                    {transaction.city} - {transaction.district}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color={transaction.amount > 0 ? 'success.main' : 'error.main'}
                                >
                                  {transaction.amount > 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={transaction.status === 'paid' ? 'Оплачено' : transaction.status}
                                  size="small"
                                  color={transaction.status === 'paid' ? 'success' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </LocalizationProvider>
  );
};

export default UserBalanceHistory;
